import { useId, useRef, type KeyboardEvent, type ReactNode } from 'react';
import { cn } from './lib/cn';

export interface TabItem {
  id: string;
  label: string;
  count?: number;
  icon?: ReactNode;
  disabled?: boolean;
  panelId?: string;
}

export interface TabsProps {
  items: TabItem[];
  activeId: string;
  onChange: (id: string) => void;
  className?: string;
  ariaLabel?: string;
}

export function Tabs({ items, activeId, onChange, className, ariaLabel = 'Seções' }: TabsProps) {
  const instanceId = useId();
  const tabRefs = useRef(new Map<string, HTMLButtonElement>());

  const selectRelativeTab = (currentId: string, direction: 1 | -1) => {
    const enabledItems = items.filter((item) => !item.disabled);
    const currentIndex = enabledItems.findIndex((item) => item.id === currentId);
    if (currentIndex < 0 || enabledItems.length === 0) return;
    const nextIndex = (currentIndex + direction + enabledItems.length) % enabledItems.length;
    const nextItem = enabledItems[nextIndex];
    if (!nextItem) return;
    tabRefs.current.get(nextItem.id)?.focus();
    onChange(nextItem.id);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLButtonElement>, tab: TabItem) => {
    if (event.key === 'ArrowRight') {
      event.preventDefault();
      selectRelativeTab(tab.id, 1);
    } else if (event.key === 'ArrowLeft') {
      event.preventDefault();
      selectRelativeTab(tab.id, -1);
    } else if (event.key === 'Home' || event.key === 'End') {
      event.preventDefault();
      const enabledItems = items.filter((item) => !item.disabled);
      const nextItem =
        event.key === 'Home' ? enabledItems[0] : enabledItems[enabledItems.length - 1];
      if (!nextItem) return;
      tabRefs.current.get(nextItem.id)?.focus();
      onChange(nextItem.id);
    }
  };

  return (
    <div
      role="tablist"
      aria-label={ariaLabel}
      className={cn(
        'flex max-w-full items-center gap-1.5 overflow-x-auto rounded-2xl border border-[var(--border)] bg-[var(--input)] p-1.5',
        className,
      )}
    >
      {items.map((tab) => {
        const isActive = tab.id === activeId;
        return (
          <button
            key={tab.id}
            ref={(node) => {
              if (node) tabRefs.current.set(tab.id, node);
              else tabRefs.current.delete(tab.id);
            }}
            type="button"
            role="tab"
            id={`${instanceId}-${tab.id}-tab`}
            aria-selected={isActive}
            aria-controls={tab.panelId}
            tabIndex={isActive ? 0 : -1}
            disabled={tab.disabled}
            onClick={() => onChange(tab.id)}
            onKeyDown={(event) => handleKeyDown(event, tab)}
            className={cn(
              'flex min-h-11 shrink-0 select-none items-center gap-2 rounded-xl border border-transparent px-3.5 py-2 text-xs font-extrabold transition-[background-color,color,border-color,box-shadow] duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring)] focus-visible:ring-offset-1 focus-visible:ring-offset-[var(--input)] disabled:pointer-events-none disabled:opacity-45',
              isActive
                ? 'motion-tab-selected border-[var(--border)] bg-[var(--surface)] text-[var(--foreground)] shadow-[var(--shadow-sm)]'
                : 'text-[var(--muted-foreground)] hover:bg-[var(--surface-raised)] hover:text-[var(--foreground)]',
            )}
          >
            {tab.icon}
            <span>{tab.label}</span>
            {tab.count !== undefined && (
              <span
                className={cn(
                  'rounded-md px-1.5 py-0.5 text-[10px] font-black',
                  isActive
                    ? 'bg-[var(--accent-soft)] text-[var(--accent)]'
                    : 'bg-[var(--surface-raised)] text-[var(--muted-foreground)]',
                )}
              >
                {tab.count}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}
